import React, { useEffect, useState } from 'react';
import { useT } from './Chrome';

///////////////////////////////////////////////////////////////////////////////
// Optional SMS verification of the receiver's number (Twilio Verify, see
// /api/phone-verify/*). Purely a trust badge — creating a Top Me Up link works
// without it. If the server answers 503 (not configured) this renders nothing.
///////////////////////////////////////////////////////////////////////////////

async function call(url, body) {
  const r = await fetch(url, body
    ? { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) }
    : undefined);
  let data = null;
  try { data = await r.json(); } catch (e) { /* non-JSON body */ }
  return { ok: r.ok, status: r.status, data };
}

export default function TmuPhoneVerify({ phone }) {
  const t = useT();
  const [state, setState] = useState('idle'); // idle | sending | sent | checking | verified | unavailable
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);

  const digits = String(phone || '').replace(/\D/g, '');
  const ready = digits.length >= 8;

  // Editing the number invalidates any code/verified state; look the number
  // up in case it was already verified earlier.
  useEffect(() => {
    setState((s) => (s === 'unavailable' ? s : 'idle'));
    setCode('');
    setError(null);
    if (!ready) return undefined;
    let dead = false;
    const timer = setTimeout(() => {
      call(`/api/phone-verify/status?phone=${encodeURIComponent(String(phone).trim())}`)
        .then((res) => {
          if (dead) return;
          if (res.status === 503) setState('unavailable');
          else if (res.ok && res.data && res.data.verified) setState('verified');
        })
        .catch(() => {});
    }, 700);
    return () => { dead = true; clearTimeout(timer); };
  }, [phone]);

  if (!ready || state === 'unavailable') return null;

  function errorText(res) {
    return res.status === 429 ? t('tmu_verify_too_many') : t('tmu_verify_send_failed');
  }

  async function send() {
    setError(null);
    setState('sending');
    try {
      const res = await call('/api/phone-verify/send', { phone: String(phone).trim() });
      if (res.status === 503) { setState('unavailable'); return; }
      if (!res.ok) { setError(errorText(res)); setState('idle'); return; }
      setCode('');
      setState('sent');
    } catch (e) {
      setError(t('tmu_verify_send_failed'));
      setState('idle');
    }
  }

  async function check() {
    setError(null);
    setState('checking');
    try {
      const res = await call('/api/phone-verify/check', { phone: String(phone).trim(), code: code.trim() });
      if (res.status === 503) { setState('unavailable'); return; }
      if (res.ok && res.data && res.data.verified) { setState('verified'); return; }
      setError(res.status === 429 ? t('tmu_verify_too_many') : t('tmu_verify_failed'));
      setState('sent');
    } catch (e) {
      setError(t('tmu_verify_failed'));
      setState('sent');
    }
  }

  if (state === 'verified') {
    return <div className="tmu-verify"><span className="tag good"><span className="tag-ico">✓</span> {t('tmu_verified')}</span></div>;
  }

  return (
    <div className="tmu-verify">
      {(state === 'idle' || state === 'sending') && (
        <>
          <button type="button" className="linkish" onClick={send} disabled={state === 'sending'}>
            {state === 'sending' ? t('tmu_verify_sending') : t('tmu_verify_cta')}
          </button>
          <div className="cohint">{t('tmu_verify_why')}</div>
        </>
      )}
      {(state === 'sent' || state === 'checking') && (
        <>
          <div className="cohint">{t('tmu_verify_code_sent')}</div>
          <div className="tmu-verify-row">
            <input
              className="coinput tmu-verify-code" type="text" inputMode="numeric" autoComplete="one-time-code"
              maxLength={6} value={code} placeholder="123456" aria-label={t('tmu_verify_code_label')}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <button type="button" className="btn small" onClick={check} disabled={state === 'checking' || code.length !== 6}>
              {state === 'checking' ? t('tmu_verify_checking') : t('tmu_verify_confirm')}
            </button>
            <button type="button" className="linkish" onClick={send} disabled={state === 'checking'}>{t('tmu_verify_resend')}</button>
          </div>
        </>
      )}
      {error && <div className="notice bad payerr" style={{ marginTop: 6 }}>{error}</div>}
    </div>
  );
}
